import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { roleLabel } from "../../../utils/roleLabels";

/**
 * RoleChips - one user's roles, as a set rather than a single value.
 *
 * The column this replaces was a Select bound to `roleId`. A Select can hold
 * exactly one value, so a user who holds AD and CCO could only ever be shown
 * as one of them, and picking a role in the dropdown read as REPLACING what
 * was there. Adding a role is now visibly additive: the held roles are chips,
 * and the picker only offers what is not already held.
 *
 * Platform Manager is a singleton and is never revoked from here. Leaving the
 * platform with no AD is unrecoverable, so its chip offers Transfer instead.
 */

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 8px",
  borderRadius: 12,
  background: "#eef2ff",
  color: "#3730a3",
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const actionStyle = {
  minWidth: "auto",
  fontSize: 11,
  padding: "0 4px",
  textTransform: "none",
};

/**
 * heldRoles - the role codes a row carries, tolerating a users list that has
 * not grown `roles` yet: a pre-multi-role API still answers with `roleId`
 * only, and a user showing no role at all would read as "no access".
 */
export function heldRoles(row, catalog) {
  const codes = Array.isArray(row?.roles) && row.roles.length
    ? Array.from(new Set(row.roles))
    : null;
  if (codes) {
    return codes.map((code) => {
      const entry = (catalog || []).find((r) => r.code === code);
      return { code, display_name: entry?.display_name || roleLabel(code) };
    });
  }
  if (row?.roleId == null) return [];
  const entry = (catalog || []).find(
    (r) => Number(r.legacy_role_id) === Number(row.roleId)
  );
  if (!entry) return [];
  return [{ code: entry.code, display_name: entry.display_name }];
}

function addableRoles(catalog, heldCodes) {
  return (catalog || []).filter(
    (r) => r.assignable && !heldCodes.includes(r.code)
  );
}

/**
 * Row height for the Roles cell. The grid this cell lives in (x-data-grid
 * 5.10) draws every row at one fixed height and clips the rest, so a cell
 * cannot simply wrap: the 2026-09 team pass found the second chip of a
 * two-role user cut off, with its Revoke unreachable. Each role now sits on
 * its own line, the picker on the last one, and the grid asks here how tall
 * the row has to be. The numbers are the drawn heights: a chip line is
 * about 28px, the picker about 33px, both inside a 34px line.
 */
export const ROLE_LINE_PX = 34;
export const ROLE_CELL_PAD_PX = 12;
export const GRID_ROW_MIN_PX = 52;

export function roleCellLines(row, catalog, isAD) {
  const held = heldRoles(row, catalog);
  const heldCodes = held.map((r) => r.code);
  const picker = isAD && addableRoles(catalog, heldCodes).length > 0;
  // "No role" and the picker share one line; a held role never shares.
  const trailer = picker || held.length === 0 ? 1 : 0;
  return held.length + trailer;
}

export function roleRowHeight(row, catalog, isAD) {
  return Math.max(
    GRID_ROW_MIN_PX,
    ROLE_CELL_PAD_PX + roleCellLines(row, catalog, isAD) * ROLE_LINE_PX
  );
}

function RoleChips({
  row,
  catalog,
  isAD,
  onAddRole,
  onDeactivate,
  onTransferAd,
}) {
  const held = heldRoles(row, catalog);
  const heldCodes = held.map((r) => r.code);
  const addable = addableRoles(catalog, heldCodes);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "6px",
        width: "100%",
        py: "6px",
      }}
    >
      {held.map((r) => (
        <span key={r.code} style={chipStyle}>
          {r.display_name}
          {isAD && r.code === "AD" && (
            <Button
              size="small"
              color="primary"
              aria-label={`Transfer ${r.display_name}`}
              onClick={() => onTransferAd(row)}
              sx={actionStyle}
            >
              Transfer
            </Button>
          )}
          {isAD && r.code !== "AD" && (
            <>
              <Button
                size="small"
                color="warning"
                aria-label={`Suspend ${r.display_name}`}
                onClick={() => onDeactivate(row.id, "SUSPEND", r.code)}
                sx={actionStyle}
              >
                Suspend
              </Button>
              <Button
                size="small"
                color="error"
                aria-label={`Revoke ${r.display_name}`}
                onClick={() => onDeactivate(row.id, "REVOKE", r.code)}
                sx={actionStyle}
              >
                Revoke
              </Button>
            </>
          )}
        </span>
      ))}

      {(held.length === 0 || (isAD && addable.length > 0)) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {held.length === 0 && <span style={{ fontSize: 12, color: "#6b7280" }}>No role</span>}
          {isAD && addable.length > 0 && (
            <Select
              value=""
              displayEmpty
              onChange={(event) => onAddRole(event.target.value, row.id, heldCodes)}
              className="role-dropdown"
              inputProps={{ "aria-label": "Add role" }}
              sx={{
                minWidth: 140,
                "& .MuiSelect-select": { fontSize: 13, padding: "6px 10px", minHeight: "auto" },
              }}
            >
              <MenuItem value="" disabled sx={{ fontSize: 13 }}>
                Add role
              </MenuItem>
              {addable.map((role) => (
                <MenuItem
                  key={role.legacy_role_id ?? role.code}
                  value={role.legacy_role_id}
                  sx={{ fontSize: 13 }}
                >
                  {role.display_name}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      )}
    </Box>
  );
}

export default RoleChips;
